import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, Inject } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { Tpl3plService, ExportQueueItem, Tpl3plQueueStatsCounts, Tpl3plSimulatedAvailabilityLine, Tpl3plSimulatedAvailabilityResponse } from '../../../services/tpl3pl.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { OrdersService } from '../../../services/orders.service';
import { RawResponseModalComponent } from '../shared/raw-response-modal/raw-response-modal.component';

@Component({
	selector: 'app-order-detail-dialog',
	template: `
		<div class="order-detail-dialog">
			<div class="dialog-header">
				<h2 class="dialog-title">
					Order <span class="order-no">{{ data.orderno }}</span>
					<span *ngIf="order?.header?.statusname" class="order-status-pill" [ngClass]="statusClass(order?.header?.orderstatusid)">
						{{ order.header.statusname }}
					</span>
				</h2>
				<button mat-icon-button (click)="close()" class="close-btn"><mat-icon>close</mat-icon></button>
			</div>
			<div class="dialog-body">
				<div *ngIf="loading" class="loading-state">
					<mat-spinner diameter="40"></mat-spinner>
					<p>Loading order…</p>
				</div>
				<div *ngIf="error && !loading" class="error-state">
					<mat-icon color="warn">error_outline</mat-icon>
					<p>{{ error }}</p>
				</div>
				<ng-container *ngIf="order && !loading">
					<div class="order-meta-row">
						<div class="meta-item" *ngIf="order.header?.shippername"><span class="meta-label">Ship Via</span><span>{{ order.header.shippername }}</span></div>
						<div class="meta-item" *ngIf="order.header?.datewanted"><span class="meta-label">Date Wanted</span><span>{{ order.header.datewanted }}</span></div>
						<div class="meta-item" *ngIf="order.header?.enteredby"><span class="meta-label">Entered By</span><span>{{ order.header.enteredby }}</span></div>
						<div class="meta-item" *ngIf="order.header?.date_created"><span class="meta-label">Created</span><span>{{ order.header.date_created }}</span></div>
					</div>
					<div class="order-address-row" *ngIf="order.header?.shipto || order.header?.comments">
						<div class="addr-block" *ngIf="order.header?.shipto">
							<div class="meta-label">Ship To</div>
							<div [innerHTML]="order.header.shipto"></div>
						</div>
						<div class="addr-block" *ngIf="order.header?.contactphone">
							<div class="meta-label">Phone</div>
							<div>{{ order.header.contactphone }}</div>
							<div *ngIf="order.header?.contactemail" class="mt-1">
								<div class="meta-label">Email</div>
								<div>{{ order.header.contactemail }}</div>
							</div>
						</div>
						<div class="addr-block" *ngIf="order.header?.comments">
							<div class="meta-label">Notes</div>
							<div>{{ order.header.comments }}</div>
						</div>
					</div>
					<div class="small text-muted mb-2" *ngIf="simulatedAvailabilityLoccode && order.details?.length">
						Avail. (sim.) uses fillability warehouse {{ simulatedAvailabilityLoccode }} and global queue simulation.
					</div>
					<table class="order-lines-table" *ngIf="order.details?.length">
						<thead>
							<tr>
								<th>Item</th>
								<th>Description</th>
								<th class="num">QOH</th>
								<th class="num" [matTooltip]="simulatedAvailableTooltip">Avail. (sim.)</th>
								<th class="num">Ordered</th>
								<th class="num">Invoiced</th>
								<th class="num">Outstanding</th>
								<th class="num">Price</th>
								<th class="num">Total</th>
							</tr>
						</thead>
						<tbody>
							<tr *ngFor="let item of order.details">
								<td>{{ item.stkcode }}</td>
								<td>{{ item.description }}</td>
								<td class="num">{{ item.qoh }}</td>
								<td class="num">{{ item.simulated_available != null ? item.simulated_available : '—' }}</td>
								<td class="num">{{ item.ordered }}</td>
								<td class="num">{{ item.qtyinvoiced }}</td>
								<td class="num">{{ item.ordered - item.qtyinvoiced }}</td>
								<td class="num">{{ item.unitprice | currency }}</td>
								<td class="num">{{ item.discountprice * item.ordered | currency }}</td>
							</tr>
						</tbody>
						<tfoot *ngIf="order.saved_totals?.length">
							<tr *ngFor="let t of order.saved_totals">
								<td colspan="7"></td>
								<td class="num total-label">{{ t.title }}:</td>
								<td class="num">{{ t.text | currency }}</td>
							</tr>
						</tfoot>
					</table>
				</ng-container>
			</div>
			<div class="dialog-footer">
				<button mat-stroked-button (click)="goToOrder()">
					<mat-icon>open_in_new</mat-icon> Open full order
				</button>
				<button mat-button (click)="close()">Close</button>
			</div>
		</div>
	`,
	styles: [`
		.order-detail-dialog { display: flex; flex-direction: column; height: 100%; }
		.dialog-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px 12px; border-bottom: 1px solid #dee2e6; }
		.dialog-title { margin: 0; font-size: 18px; font-weight: 600; display: flex; align-items: center; gap: 10px; }
		.order-no { color: #084c8c; }
		.close-btn { margin-left: auto; }
		.dialog-body { flex: 1; overflow-y: auto; padding: 16px 20px; }
		.dialog-footer { display: flex; align-items: center; justify-content: flex-end; gap: 8px; padding: 10px 20px; border-top: 1px solid #dee2e6; }
		.loading-state, .error-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px; gap: 12px; color: #6c757d; }
		.order-meta-row { display: flex; flex-wrap: wrap; gap: 16px; margin-bottom: 14px; padding-bottom: 12px; border-bottom: 1px solid #f0f0f0; }
		.meta-item { display: flex; flex-direction: column; gap: 2px; min-width: 120px; }
		.meta-label { font-size: 11px; font-weight: 600; text-transform: uppercase; color: #6c757d; letter-spacing: 0.4px; }
		.order-address-row { display: flex; flex-wrap: wrap; gap: 20px; margin-bottom: 16px; padding-bottom: 14px; border-bottom: 1px solid #f0f0f0; }
		.addr-block { min-width: 160px; font-size: 13px; }
		.order-lines-table { width: 100%; border-collapse: collapse; font-size: 13px; }
		.order-lines-table th { text-align: left; padding: 6px 8px; border-bottom: 2px solid #dee2e6; font-size: 11px; font-weight: 600; text-transform: uppercase; color: #6c757d; white-space: nowrap; }
		.order-lines-table td { padding: 5px 8px; border-bottom: 1px solid #f0f0f0; }
		.order-lines-table .num { text-align: right; }
		.order-lines-table tfoot td { font-weight: 600; border-top: 2px solid #dee2e6; }
		.total-label { color: #495057; }
		.order-status-pill { display: inline-block; padding: 2px 9px; border-radius: 10px; font-size: 12px; font-weight: 500; border: 1px solid transparent; }
		.erp-status-open        { background: #e7f4ff; color: #084c8c; border-color: #b3ddff; }
		.erp-status-invoiced    { background: #e9f7ef; color: #0b5d1e; border-color: #bfe7cf; }
		.erp-status-cancelled   { background: #fdecea; color: #8a1c1c; border-color: #f5c2c0; }
		.erp-status-credit-hold { background: #fff3e0; color: #b34a00; border-color: #ffcc80; }
		.erp-status-preauth-authorized { background: #76ffa1; color: #0d0d0d; border-color: #3dcc6e; }
		.erp-status-preauth-decline    { background: #ff006c; color: #fff; border-color: #c2004f; }
		.erp-status-default     { background: #f1f3f5; color: #495057; border-color: #e9ecef; }
	`]
})
export class OrderDetailDialogComponent {
	order: any = null;
	loading = true;
	error: string | null = null;
	simulatedAvailabilityLoccode = '';

	constructor(
		public dialogRef: MatDialogRef<OrderDetailDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: { orderno: string },
		private ordersService: OrdersService,
		private tpl3plService: Tpl3plService,
		private router: Router
	) {
		this.ordersService.getOrder(data.orderno).subscribe({
			next: (o: any) => {
				if (o && (!Array.isArray(o.details) || o.details.length === 0)) {
					this.ordersService.getInvoicedOrder(data.orderno).subscribe({
						next: (inv: any) => {
							this.order = (inv && (Array.isArray(inv.details) ? inv.details.length > 0 : inv.header)) ? inv : o;
							this.loadSimulatedAvailability();
						},
						error: () => {
							this.order = o;
							this.loadSimulatedAvailability();
						}
					});
				} else {
					this.order = o;
					this.loadSimulatedAvailability();
				}
			},
			error: () => { this.error = 'Could not load order ' + data.orderno; this.loading = false; }
		});
	}

	private loadSimulatedAvailability(): void {
		this.tpl3plService.getSimulatedLineAvailability(this.data.orderno).subscribe({
			next: (sim: Tpl3plSimulatedAvailabilityResponse) => {
				this.applySimulatedAvailability(sim);
				this.loading = false;
			},
			error: () => { this.loading = false; }
		});
	}

	get simulatedAvailableTooltip(): string {
		const warehouse = this.simulatedAvailabilityLoccode ? this.simulatedAvailabilityLoccode : 'fillability warehouse';
		return 'Simulated remaining QOH when this line is reached in the global open-order queue walk (' + warehouse + ').';
	}

	private applySimulatedAvailability(sim: Tpl3plSimulatedAvailabilityResponse): void {
		if (!sim || !sim.success || !this.order || !Array.isArray(this.order.details)) {
			return;
		}
		this.simulatedAvailabilityLoccode = sim.fillability_loccode || '';
		const lineMap: { [key: string]: Tpl3plSimulatedAvailabilityLine } = {};
		for (const line of (sim.lines || [])) {
			const k = this.lineKey(line.orderlineno, line.stkcode);
			lineMap[k] = line;
		}
		for (const detail of this.order.details) {
			const k = this.lineKey(detail.orderlineno, detail.stkcode);
			const simLine = lineMap[k];
			if (simLine) {
				detail.simulated_available = simLine.simulated_available;
			}
		}
	}

	private lineKey(orderlineno: any, stkcode: any): string {
		const line = String(orderlineno == null ? '' : orderlineno).trim();
		const sku = String(stkcode == null ? '' : stkcode).trim();
		return line + '|' + sku;
	}

	statusClass(id: any): string {
		const n = parseInt(id, 10);
		if (isNaN(n)) { return 'erp-status-default'; }
		if (n === 80) { return 'erp-status-credit-hold'; }
		if (n === 53) { return 'erp-status-preauth-authorized'; }
		if (n === 52) { return 'erp-status-preauth-decline'; }
		if (n >= 90) { return 'erp-status-cancelled'; }
		if (n > 29 && n < 51) { return 'erp-status-invoiced'; }
		if (n <= 29) { return 'erp-status-open'; }
		return 'erp-status-default';
	}

	goToOrder(): void {
		this.dialogRef.close();
		this.router.navigate(['/orders/order-inquiry'], { queryParams: { orderno: this.data.orderno } });
	}

	close(): void { this.dialogRef.close(); }
}

export interface ForceErpStatusDialogData {
	orderno: string;
	currentStatusId: string;
	statuses: Array<{ order_status_id: number | string; name: string }>;
}

@Component({
	selector: 'app-force-erp-status-dialog',
	template: `
		<h2 mat-dialog-title>Set ERP order status</h2>
		<mat-dialog-content>
			<p class="small text-muted mb-2">Order <span class="text-monospace">{{ data.orderno }}</span></p>
			<p class="small text-muted mb-3">
				Updates <code>salesorders.orderstatusid</code> only. This does not run dispatch routes, picking-freeze, or packing-slip logic.
			</p>
			<mat-form-field appearance="fill" class="w-100">
				<mat-label>Status ID</mat-label>
				<mat-select [(ngModel)]="selectedId">
					<mat-option *ngFor="let s of data.statuses" [value]="toNum(s.order_status_id)">
						{{ s.name }} ({{ s.order_status_id }})
					</mat-option>
				</mat-select>
			</mat-form-field>
		</mat-dialog-content>
		<mat-dialog-actions align="end">
			<button mat-button type="button" mat-dialog-close>Cancel</button>
			<button mat-raised-button color="primary" type="button" (click)="save()" [disabled]="selectedId == null || selectedId <= 0 || saving">
				<mat-spinner *ngIf="saving" diameter="20" class="d-inline-block mr-1"></mat-spinner>
				{{ saving ? 'Saving…' : 'Save' }}
			</button>
		</mat-dialog-actions>
	`,
	styles: [`
		.w-100 { width: 100%; }
		code { font-size: 12px; }
	`]
})
export class ForceErpStatusDialogComponent {
	selectedId: number | null = null;
	saving = false;

	constructor(
		public dialogRef: MatDialogRef<ForceErpStatusDialogComponent, { success: boolean; erp_order_status_id?: string; erp_order_status_name?: string } | undefined>,
		@Inject(MAT_DIALOG_DATA) public data: ForceErpStatusDialogData,
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService
	) {
		const n = parseInt(String(data.currentStatusId || '').trim(), 10);
		this.selectedId = isNaN(n) ? null : n;
	}

	toNum(v: number | string): number {
		const n = parseInt(String(v), 10);
		return isNaN(n) ? 0 : n;
	}

	save(): void {
		if (this.selectedId == null || this.selectedId <= 0 || !this.data.orderno) {
			return;
		}
		this.saving = true;
		this.tpl3plService.setErpOrderStatus(this.data.orderno, this.selectedId).subscribe({
			next: (r) => {
				this.saving = false;
				if (r.success) {
					this.globalSearchService.showNotification(r.message || 'ERP status updated', 'success', 'bottom', 'right');
					this.dialogRef.close({
						success: true,
						erp_order_status_id: r.erp_order_status_id,
						erp_order_status_name: r.erp_order_status_name,
					});
				} else {
					this.globalSearchService.showNotification(r.message || 'Could not update status', 'warning', 'bottom', 'right');
				}
			},
			error: () => {
				this.saving = false;
				this.globalSearchService.showNotification('Request failed', 'danger', 'bottom', 'right');
			}
		});
	}
}

export interface RawPushResultRow {
	orderno: string;
	success: boolean;
	message: string;
	fullResult: any;
}

@Component({
	selector: 'app-tpl3pl-export-queue',
	templateUrl: './tpl3pl-export-queue.component.html',
	styleUrls: ['./tpl3pl-export-queue.component.scss']
})
export class Tpl3plExportQueueComponent implements OnInit, OnDestroy, AfterViewInit {
	@ViewChild(MatPaginator) paginator!: MatPaginator;
	@ViewChild(MatSort) sort!: MatSort;

	displayedColumns: string[] = ['select', 'orderno', 'debtorno', 'customer_name', 'payterms', 'tpl3pl_remote_order_id', 'export_status', 'state', 'erp_status', 'erp_fillability', 'placedby', 'enteredby', 'created_at', 'export_attempts', 'last_export_attempt', 'error_message', 'actions'];
	dataSource = new MatTableDataSource<ExportQueueItem>();
	/** Pending / failed rows the user can include in a manual batch export. */
	readonly selection = new SelectionModel<ExportQueueItem>(true, [], true, (a, b) => a.id === b.id);
	private allItems: ExportQueueItem[] = [];
	loading = false;
	processing = false;
	batchDeleting = false;
	exportingOrderNo: string | null = null;
	/** Server: export_status IN (...); empty = all statuses. */
	exportStatusFilters: string[] = [];
	/** Server: state IN (...); empty = all. */
	queueStateFilters: string[] = [];
	/** Exact order number (server). */
	orderFilter = '';
	/** Server: orderno LIKE %…% */
	orderContainsFilter = '';
	pgsIdContains = '';
	errorContains = '';
	erpStatusFilters: number[] = [];
	/** Client-side on the loaded page of rows (fillability is computed after fetch). */
	fillabilityFilters: string[] = [];
	orderStatuses: any[] = [];
	includeArchived = false;
	lastExportResult: any = null;
	previewLoadingOrderNo: string | null = null;
	selectedErrorItem: ExportQueueItem | null = null;
	selectedErrorDetails: {
		message: string;
		errorCode?: string;
		canRetry: boolean;
		response?: any;
		circuitOpen?: boolean;
	} | null = null;
	rawPushInProgress = false;
	rawPushProgress = '';
	rawPushLimit = 10;
	rawPushResults: RawPushResultRow[] = [];
	rawPushResultsDataSource = new MatTableDataSource<RawPushResultRow>([]);
	rawPushResultsColumns: string[] = ['orderno', 'status', 'message', 'view_raw'];

	massStatusUpdating = false;
	/** Progress text while sequential PGS batches run (e.g. "Processed 75 order(s)…"). */
	massStatusProgress = '';
	/** Max orders per HTTP request; client chains requests until PGS queue is exhausted. */
	pgsMassBatchSize = 25;
	massStatusResult: { checked: number; updated: number; unchanged: number; failed: number; erp_status_updated?: number; details: any[]; batch_count?: number } | null = null;

	readonly exportStatusOptions: { value: string; label: string }[] = [
		{ value: 'pending', label: 'Pending' },
		{ value: 'exporting', label: 'Exporting' },
		{ value: 'exported', label: 'Exported' },
		{ value: 'failed', label: 'Failed' },
		{ value: 'dead_letter', label: 'Dead letter' },
	];

	readonly queueStateOptions: { value: string; label: string }[] = [
		{ value: 'Created', label: 'Created' },
		{ value: 'Validated', label: 'Validated' },
		{ value: 'Queued', label: 'Queued' },
		{ value: 'Exporting', label: 'Exporting' },
		{ value: 'Submitted', label: 'Submitted' },
		{ value: 'Dispatched', label: 'Dispatched' },
		{ value: 'Shipped', label: 'Shipped' },
		{ value: 'Invoiced', label: 'Invoiced' },
		{ value: 'Failed', label: 'Failed' },
		{ value: 'DeadLetter', label: 'Dead letter' },
		{ value: 'Cancelled', label: 'Cancelled' },
		{ value: 'BackOrder', label: 'Back order' },
	];

	readonly fillabilityOptions: { value: string; label: string }[] = [
		{ value: 'fillable', label: 'Fillable' },
		{ value: 'partial', label: 'Partially fillable' },
		{ value: 'not_fillable', label: 'Not fillable' },
		{ value: 'unknown', label: 'Unknown' },
	];

	get eligibleOrdersCount(): number {
		const data = this.dataSource.data || [];
		return data.filter((r: ExportQueueItem) => r.export_status === 'pending' || r.export_status === 'failed').length;
	}

	isRowBatchEligible(item: ExportQueueItem): boolean {
		return item.export_status === 'pending' || item.export_status === 'failed';
	}

	/** Same rule as the per-row Remove action (batch remove uses this). */
	canRemoveFromQueue(item: ExportQueueItem): boolean {
		const s = item.export_status;
		return s === 'failed' || s === 'pending' || s === 'dead_letter';
	}

	/** Checkbox: pending/failed (export) or dead letter (remove only). */
	isRowCheckboxSelectable(item: ExportQueueItem): boolean {
		return this.canRemoveFromQueue(item);
	}

	getCheckboxSelectableLoadedRows(): ExportQueueItem[] {
		return (this.dataSource.data || []).filter((r) => this.isRowCheckboxSelectable(r));
	}

	get removableOrdersCount(): number {
		return this.getCheckboxSelectableLoadedRows().length;
	}

	getEligibleLoadedRows(): ExportQueueItem[] {
		return (this.dataSource.data || []).filter((r) => this.isRowBatchEligible(r));
	}

	get selectedBatchEligible(): ExportQueueItem[] {
		return this.selection.selected.filter((r) => this.isRowBatchEligible(r));
	}

	get selectedBatchEligibleCount(): number {
		return this.selectedBatchEligible.length;
	}

	isAllCheckboxRowsSelected(): boolean {
		const rows = this.getCheckboxSelectableLoadedRows();
		return rows.length > 0 && rows.every((r) => this.selection.isSelected(r));
	}

	isCheckboxRowsSelectionIndeterminate(): boolean {
		const rows = this.getCheckboxSelectableLoadedRows();
		const n = rows.filter((r) => this.selection.isSelected(r)).length;
		return n > 0 && n < rows.length;
	}

	onMasterCheckboxChange(checked: boolean): void {
		const rows = this.getCheckboxSelectableLoadedRows();
		if (rows.length === 0) {
			return;
		}
		if (checked) {
			rows.forEach((r) => this.selection.select(r));
		} else {
			rows.forEach((r) => this.selection.deselect(r));
		}
	}

	setRowCheckboxSelected(item: ExportQueueItem, checked: boolean): void {
		if (!this.isRowCheckboxSelectable(item)) {
			return;
		}
		if (checked) {
			this.selection.select(item);
		} else {
			this.selection.deselect(item);
		}
	}

	get selectedRemovableFromQueue(): ExportQueueItem[] {
		return this.selection.selected.filter((r) => this.canRemoveFromQueue(r));
	}

	get selectedRemovableCount(): number {
		return this.selectedRemovableFromQueue.length;
	}

	clearBatchSelection(): void {
		this.selection.clear();
	}
	constructor(
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService,
		private router: Router,
		private route: ActivatedRoute,
		private dialog: MatDialog,
		private ordersService: OrdersService
	) {}

	get orderStatusesForSelect(): any[] {
		return [...this.orderStatuses]
			.filter((s) => s && !isNaN(Number(s.order_status_id)))
			.sort((a, b) => Number(a.order_status_id) - Number(b.order_status_id));
	}

	ngOnInit(): void {
		this.ordersService.getOrderStatuses().subscribe({
			next: (r: any) => {
				this.orderStatuses = Array.isArray(r) ? r : (r && Array.isArray(r.data) ? r.data : []);
			},
			error: () => {
				this.orderStatuses = [];
			}
		});
		this.loadQueue();
	}

	ngAfterViewInit(): void {
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
	}

	queueTotal: number | null = null;
	queueStats: Tpl3plQueueStatsCounts | null = null;
	statsLoading = false;
	/** True when fillability filter hit the server scan cap (more matches may exist). */
	fillabilityScanCapped = false;
	private serverFilterReloadTimer: ReturnType<typeof setTimeout> | null = null;

	loadQueue(preservePage = false): void {
		this.refreshQueueStats();
		this.loading = true;
		this.selection.clear();
		this.selectedErrorItem = null;
		this.selectedErrorDetails = null;
		if (!preservePage && this.dataSource.paginator) {
			this.dataSource.paginator.firstPage();
		}
		const pageLimit = this.fillabilityFilters.length > 0 ? 500 : 100;
		const filters: any = { limit: pageLimit, offset: 0, includeArchived: this.includeArchived };
		if (this.exportStatusFilters.length > 0) {
			filters.statuses = [...this.exportStatusFilters];
		}
		if (this.queueStateFilters.length > 0) {
			filters.states = [...this.queueStateFilters];
		}
		const orderSearch = (this.orderFilter || '').trim();
		if (orderSearch) {
			filters.orderno = orderSearch;
		}
		const orderContains = (this.orderContainsFilter || '').trim();
		if (orderContains) {
			filters.orderno_contains = orderContains;
		}
		const pgsPart = (this.pgsIdContains || '').trim();
		if (pgsPart) {
			filters.pgs_order_id_contains = pgsPart;
		}
		const errPart = (this.errorContains || '').trim();
		if (errPart) {
			filters.error_contains = errPart;
		}
		if (this.erpStatusFilters.length > 0) {
			filters.erp_status_ids = [...this.erpStatusFilters];
		}
		if (this.fillabilityFilters.length > 0) {
			filters.fillabilities = [...this.fillabilityFilters];
		}

		this.tpl3plService.getExportQueue(filters).subscribe({
			next: (result) => {
				this.allItems = result.data || [];
				this.queueTotal = result.total ?? null;
				this.fillabilityScanCapped = !!result.fillability_scan_capped;
				this.applyFilters(!preservePage);
				this.loading = false;
			},
			error: () => {
				this.globalSearchService.showNotification('Error loading export queue', 'danger', 'bottom', 'right');
				this.loading = false;
			}
		});
	}

	private refreshQueueStats(): void {
		const firstLoad = this.queueStats === null;
		if (firstLoad) {
			this.statsLoading = true;
		}
		this.tpl3plService.getQueueStats().subscribe({
			next: (r) => {
				this.statsLoading = false;
				if (r.success && r.counts) {
					const c = r.counts;
					this.queueStats = {
						...c,
						exported_not_shipped: c.exported_not_shipped ?? 0,
						open_queue_fillability: c.open_queue_fillability ?? {
							fillable: 0,
							partial: 0,
							not_fillable: 0,
							unknown: 0,
							open_queue_orders: 0
						}
					};
				}
			},
			error: () => {
				this.statsLoading = false;
			}
		});
	}

	applyQueueFilters(): void {
		if (this.serverFilterReloadTimer) {
			clearTimeout(this.serverFilterReloadTimer);
			this.serverFilterReloadTimer = null;
		}
		this.lastExportResult = null;
		this.loadQueue();
	}

	/** Fillability is applied on the server (scans a larger window than the page size). */
	onFillabilityFilterChange(): void {
		if (this.serverFilterReloadTimer) {
			clearTimeout(this.serverFilterReloadTimer);
			this.serverFilterReloadTimer = null;
		}
		this.lastExportResult = null;
		this.loadQueue();
	}

	/** Debounced reload when export status / queue state / ERP status multi-select changes. */
	onServerQueueFiltersChange(): void {
		if (this.serverFilterReloadTimer) {
			clearTimeout(this.serverFilterReloadTimer);
		}
		this.serverFilterReloadTimer = setTimeout(() => {
			this.serverFilterReloadTimer = null;
			this.lastExportResult = null;
			this.loadQueue();
		}, 400);
	}

	/** Not exported (pending, failed, exporting, dead letter) + fillable — one click. */
	applyNotExportedFillablePreset(): void {
		if (this.serverFilterReloadTimer) {
			clearTimeout(this.serverFilterReloadTimer);
			this.serverFilterReloadTimer = null;
		}
		this.lastExportResult = null;
		this.exportStatusFilters = ['pending', 'failed', 'exporting', 'dead_letter'];
		this.fillabilityFilters = ['fillable'];
		this.loadQueue();
	}

	ngOnDestroy(): void {
		if (this.serverFilterReloadTimer) {
			clearTimeout(this.serverFilterReloadTimer);
		}
	}

	refreshQueue(): void {
		this.lastExportResult = null;
		this.loadQueue();
	}

	clearFiltersAndReload(): void {
		this.lastExportResult = null;
		this.exportStatusFilters = [];
		this.queueStateFilters = [];
		this.orderFilter = '';
		this.orderContainsFilter = '';
		this.pgsIdContains = '';
		this.errorContains = '';
		this.erpStatusFilters = [];
		this.fillabilityFilters = [];
		this.fillabilityScanCapped = false;
		this.includeArchived = false;
		this.loadQueue();
	}

dismissLastResult(): void {
	this.lastExportResult = null;
}

	private applyFilters(resetPage = true): void {
		const rows = this.allItems.slice();

		const prevIndex = resetPage ? 0 : (this.dataSource.paginator?.pageIndex ?? 0);
		const prevSize = this.dataSource.paginator?.pageSize;
		this.dataSource.data = rows;
		if (this.dataSource.paginator) {
			if (resetPage) {
				this.dataSource.paginator.firstPage();
			} else if (prevSize != null) {
				this.dataSource.paginator.pageSize = prevSize;
				const pageCount = Math.max(1, Math.ceil(rows.length / prevSize));
				this.dataSource.paginator.pageIndex = Math.min(prevIndex, pageCount - 1);
			}
		}
	}

	processQueue(): void {
		this.processing = true;
		this.lastExportResult = null;
		this.tpl3plService.processQueue().subscribe({
			next: (result) => {
				this.loadQueue(true);
				if (result.success) {
					this.lastExportResult = result;
					this.globalSearchService.showNotification('Processed ' + (result.processed || 0) + ' orders', 'success', 'bottom', 'right');
				} else {
					this.lastExportResult = null;
					this.globalSearchService.showNotification('Batch had failures – see order rows', 'warning', 'bottom', 'right');
				}
				this.processing = false;
			},
			error: (error) => {
				this.loadQueue(true);
				this.lastExportResult = null;
				this.globalSearchService.showNotification('Error processing queue', 'warning', 'bottom', 'right');
				this.processing = false;
			}
		});
	}

	retryExport(item: ExportQueueItem): void {
		this.lastExportResult = null;
		this.exportingOrderNo = item.orderno;
		this.tpl3plService.retryExport(item.id, item.orderno, false).subscribe({
			next: (result) => {
				this.exportingOrderNo = null;
				this.loadQueue(true);
				if (result.success) {
					this.globalSearchService.showNotification('Order exported', 'success', 'bottom', 'right');
				} else {
					this.lastExportResult = null;
					this.globalSearchService.showNotification('See error on order row', 'warning', 'bottom', 'right');
				}
			},
			error: (error) => {
				this.exportingOrderNo = null;
				this.loadQueue(true);
				this.lastExportResult = null;
				this.globalSearchService.showNotification('See error on order row', 'warning', 'bottom', 'right');
			}
		});
	}

	cancelOrder(item: ExportQueueItem): void {
		if (!item?.orderno) { return; }
		this.tpl3plService.cancelOrderPreview(item.orderno).subscribe({
			next: (preview) => {
				this.dialog.open(RawResponseModalComponent, {
					width: '720px',
					maxWidth: '96vw',
					maxHeight: '90vh',
					data: {
						context: 'cancel',
						orderno: item.orderno,
						export_status: item.export_status,
						content: preview.success && preview.request_payload ? { request_payload: preview.request_payload } : {},
						onSent: () => this.loadQueue(true)
					},
					panelClass: 'raw-response-dialog'
				});
			},
			error: () => {
				this.dialog.open(RawResponseModalComponent, {
					width: '720px',
					maxWidth: '96vw',
					maxHeight: '90vh',
					data: {
						context: 'cancel',
						orderno: item.orderno,
						export_status: item.export_status,
						content: {},
						onSent: () => this.loadQueue(true)
					},
					panelClass: 'raw-response-dialog'
				});
			}
		});
	}

	forceResendExported(item: ExportQueueItem): void {
		if (!item?.orderno) {
			return;
		}
		const msg =
			'Re-send order ' +
			item.orderno +
			' to the 3PL?\n\nUse when the warehouse removed it from their system. This bypasses “already shipped” and duplicate-send protection for this attempt only.';
		if (!window.confirm(msg)) {
			return;
		}
		this.lastExportResult = null;
		this.exportingOrderNo = item.orderno;
		this.tpl3plService.retryExport(item.id, item.orderno, true).subscribe({
			next: (result) => {
				this.exportingOrderNo = null;
				this.loadQueue(true);
				if (result.success) {
					this.globalSearchService.showNotification('Order re-sent to 3PL', 'success', 'bottom', 'right');
				} else {
					this.lastExportResult = null;
					this.globalSearchService.showNotification(result.message || 'Re-send failed', 'warning', 'bottom', 'right');
				}
			},
			error: () => {
				this.exportingOrderNo = null;
				this.loadQueue(true);
				this.lastExportResult = null;
				this.globalSearchService.showNotification('Re-send request failed', 'warning', 'bottom', 'right');
			}
		});
	}

	forceResendByOrderField(): void {
		const orderno = (this.orderFilter || '').trim();
		if (!orderno) {
			this.globalSearchService.showNotification('Enter an order number first', 'warning', 'bottom', 'right');
			return;
		}
		const msg =
			'Force re-send order ' +
			orderno +
			' to the 3PL?\n\nUse when the warehouse removed it from their system. This bypasses “already shipped” and duplicate-send protection for this attempt only.';
		if (!window.confirm(msg)) {
			return;
		}
		this.lastExportResult = null;
		this.exportingOrderNo = orderno;
		this.tpl3plService.retryExport(undefined, orderno, true).subscribe({
			next: (result) => {
				this.exportingOrderNo = null;
				this.loadQueue(true);
				if (result.success) {
					this.globalSearchService.showNotification('Order re-sent to 3PL', 'success', 'bottom', 'right');
				} else {
					this.lastExportResult = null;
					this.globalSearchService.showNotification(result.message || 'Re-send failed', 'warning', 'bottom', 'right');
				}
			},
			error: () => {
				this.exportingOrderNo = null;
				this.loadQueue(true);
				this.lastExportResult = null;
				this.globalSearchService.showNotification('Re-send request failed', 'warning', 'bottom', 'right');
			}
		});
	}

	exportOrder(orderno: string): void {
		this.lastExportResult = null;
		this.exportingOrderNo = orderno;
		this.tpl3plService.exportOrder(orderno).subscribe({
			next: (result) => {
				this.exportingOrderNo = null;
				this.loadQueue(true);
				if (result.success) {
					this.globalSearchService.showNotification('Order exported', 'success', 'bottom', 'right');
				} else {
					this.lastExportResult = null;
					this.globalSearchService.showNotification('See error on order row', 'warning', 'bottom', 'right');
				}
			},
			error: (error) => {
				this.exportingOrderNo = null;
				this.loadQueue(true);
				this.lastExportResult = null;
				this.globalSearchService.showNotification('See error on order row', 'warning', 'bottom', 'right');
			}
		});
	}

	getEligibleOrdersForRawPush(): ExportQueueItem[] {
		const data = this.dataSource.data || [];
		return data
			.filter((r: ExportQueueItem) => r.export_status === 'pending' || r.export_status === 'failed')
			.slice(0, this.rawPushLimit);
	}

	/** Sequential export/retry for a specific list (checkbox batch or first-N raw push). */
	private runSequentialExportBatch(eligible: ExportQueueItem[]): void {
		if (eligible.length === 0) {
			return;
		}
		this.rawPushInProgress = true;
		this.rawPushResults = [];
		this.rawPushResultsDataSource.data = [];
		const total = eligible.length;
		let index = 0;
		const run = () => {
			if (index >= eligible.length) {
				this.rawPushProgress = '';
				this.rawPushInProgress = false;
				const succeeded = this.rawPushResults.filter(r => r.success).length;
				const failed = this.rawPushResults.length - succeeded;
				if (failed === 0) {
					this.dismissRawPushResults();
				} else {
					this.rawPushResultsDataSource.data = [...this.rawPushResults];
				}
				this.loadQueue(true);
				this.globalSearchService.showNotification('Export batch finished: ' + succeeded + ' ok, ' + failed + ' failed', failed > 0 ? 'warning' : 'success', 'bottom', 'right');
				return;
			}
			const item = eligible[index];
			this.rawPushProgress = (index + 1) + ' of ' + total;
			const obs = item.export_status === 'failed' && item.id
				? this.tpl3plService.retryExport(item.id, item.orderno, false)
				: this.tpl3plService.exportOrder(item.orderno);
			obs.subscribe({
				next: (result: any) => {
					const normalized = result && typeof result === 'object' ? result : { success: false, message: 'No response' };
					this.rawPushResults.push({
						orderno: item.orderno,
						success: !!normalized.success,
						message: normalized.message || (normalized.success ? 'Exported' : 'Failed'),
						fullResult: normalized
					});
					index++;
					run();
				},
				error: (err) => {
					this.rawPushResults.push({
						orderno: item.orderno,
						success: false,
						message: err?.error?.message || err?.message || 'Request failed',
						fullResult: { success: false, message: err?.error?.message || err?.message }
					});
					index++;
					run();
				}
			});
		};
		run();
	}

	exportSelectedBatch(): void {
		const eligible = this.selectedBatchEligible;
		if (eligible.length === 0) {
			this.globalSearchService.showNotification('Select pending or failed orders to export', 'warning', 'bottom', 'right');
			return;
		}
		this.runSequentialExportBatch(eligible);
	}

	removeSelectedFromQueue(): void {
		const items = this.selectedRemovableFromQueue;
		if (items.length === 0) {
			this.globalSearchService.showNotification('Select pending, failed, or dead-letter rows to remove', 'warning', 'bottom', 'right');
			return;
		}
		const msg =
			'Remove ' +
			items.length +
			' row(s) from the export queue?\n\nOrders are not cancelled in the ERP; this only drops the 3PL queue row.';
		if (!window.confirm(msg)) {
			return;
		}
		this.batchDeleting = true;
		let index = 0;
		let removed = 0;
		let failed = 0;
		const run = (): void => {
			if (index >= items.length) {
				this.batchDeleting = false;
				this.applyFilters();
				this.refreshQueueStats();
				const tone = failed > 0 ? 'warning' : 'success';
				this.globalSearchService.showNotification(
					'Removed ' + removed + ' from queue' + (failed > 0 ? ' (' + failed + ' failed)' : ''),
					tone,
					'bottom',
					'right'
				);
				return;
			}
			const item = items[index];
			index++;
			this.tpl3plService.deleteQueueItem(item.id).subscribe({
				next: (result) => {
					if (result.success) {
						removed++;
						this.allItems = this.allItems.filter((i) => i.id !== item.id);
						this.selection.deselect(item);
						if (this.selectedErrorItem?.id === item.id) {
							this.clearErrorDetails();
						}
					} else {
						failed++;
					}
					run();
				},
				error: () => {
					failed++;
					run();
				}
			});
		};
		run();
	}

	rawManualPush(): void {
		const eligible = this.getEligibleOrdersForRawPush();
		if (eligible.length === 0) {
			this.globalSearchService.showNotification('No pending or failed orders to push', 'warning', 'bottom', 'right');
			return;
		}
		this.runSequentialExportBatch(eligible);
	}

	dismissRawPushResults(): void {
		this.rawPushResults = [];
		this.rawPushResultsDataSource.data = [];
	}

	openRawResponseForResult(row: RawPushResultRow): void {
		this.dialog.open(RawResponseModalComponent, {
			width: '720px',
			maxWidth: '96vw',
			maxHeight: '90vh',
			data: {
				orderno: row.orderno,
				export_status: row.success ? 'exported' : 'failed',
				content: row.fullResult,
				onSent: () => this.loadQueue(true)
			},
			panelClass: 'raw-response-dialog'
		});
	}

	onRawPushLimitChange(): void {}

	getErpStatusClass(statusId: string | null | undefined): string {
		const n = parseInt(statusId || '', 10);
		if (isNaN(n)) { return 'erp-status-default'; }
		if (n === 80) { return 'erp-status-credit-hold'; }
		if (n === 53) { return 'erp-status-preauth-authorized'; }
		if (n === 52) { return 'erp-status-preauth-decline'; }
		if (n >= 90) { return 'erp-status-cancelled'; }
		if (n > 29 && n < 51) { return 'erp-status-invoiced'; }
		if (n <= 29) { return 'erp-status-open'; }
		return 'erp-status-default';
	}

	getFillabilityClass(fillability: string | null | undefined): string {
		switch (fillability) {
		case 'fillable': return 'erp-fill-fillable';
		case 'partial': return 'erp-fill-partial';
		case 'not_fillable': return 'erp-fill-not-fillable';
		default: return '';
		}
	}

	getPgsStatusLabel(item: ExportQueueItem): string | null {
		if (!item.pgs_status_raw) {
			return null;
		}
		try {
			const parsed = JSON.parse(item.pgs_status_raw) as Record<string, unknown> | null;
			if (!parsed || typeof parsed !== 'object') {
				return null;
			}
			if (parsed.success === false) {
				const msg = parsed.message;
				return typeof msg === 'string' && msg.trim() !== '' ? msg : null;
			}
			let block: unknown = parsed.data ?? parsed;
			if (block && typeof block === 'object' && !Array.isArray(block)) {
				const o = block as Record<string, unknown>;
				const nested = o.Data ?? o.data;
				if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
					block = nested;
				}
			}
			if (!block || typeof block !== 'object' || Array.isArray(block)) {
				return null;
			}
			const b = block as Record<string, unknown>;
			const status =
				b.Status ??
				b.status ??
				b.OrderStatus ??
				b.orderStatus ??
				b.State ??
				b.state ??
				b.orderStatusDescription ??
				null;
			if (status == null) {
				return null;
			}
			const s = String(status).trim();
			return s !== '' ? s : null;
		} catch {
			return null;
		}
	}

	getStateBadgeClass(state: string): string {
		switch ((state || '').toLowerCase()) {
		case 'picking': return 'state-badge state-badge--picking';
		case 'dispatched': return 'state-badge state-badge--dispatched';
		case 'shipped': return 'state-badge state-badge--shipped';
		case 'invoiced': return 'state-badge state-badge--invoiced';
		case 'queued':
		case 'created':
		case 'validated': return 'state-badge state-badge--queued';
		case 'failed':
		case 'deadletter': return 'state-badge state-badge--failed';
		case 'cancelled': return 'state-badge state-badge--cancelled';
		case 'backorder': return 'state-badge state-badge--backorder';
		default: return 'state-badge state-badge--default';
		}
	}

	getStatusBadgeClass(status: string): string {
		switch (status) {
		case 'exported': return 'badge-success';
		case 'failed': return 'badge-danger';
		case 'exporting': return 'badge-warning';
		case 'dead_letter': return 'badge-secondary';
		case 'cancelled': return 'badge-warning';
		default: return 'badge-secondary';
		}
	}

	getStatusLabel(status: string): string {
		switch (status) {
		case 'pending': return 'Pending';
		case 'exporting': return 'Exporting';
		case 'exported': return 'Exported';
		case 'failed': return 'Failed';
		case 'dead_letter': return 'Dead Letter';
		case 'cancelled': return 'Cancelled';
		default: return status || '—';
		}
	}

	private static readonly ERROR_SUMMARY_MAX = 72;

	getErrorSummary(item: ExportQueueItem): string {
		if (item.export_response?.circuit_open) {
			return 'Circuit breaker open';
		}
		const msg = (item.error_message || '').trim();
		if (!msg) {
			return '';
		}
		if (msg.length <= Tpl3plExportQueueComponent.ERROR_SUMMARY_MAX) {
			return msg;
		}
		const lower = msg.toLowerCase();
		if (lower.includes('not enough quantity') || lower.includes('in inventory')) {
			const skuLike = msg.match(/\b[A-Z0-9]{6,12}\b/g);
			const uniq = skuLike ? [...new Set(skuLike)] : [];
			if (uniq.length > 2) {
				return `Quantity short (${uniq.length} SKUs) — click for details`;
			}
			if (uniq.length === 2) {
				return `Quantity short: ${uniq.join(', ')} — click for details`;
			}
			if (uniq.length === 1) {
				return `Quantity short: ${uniq[0]} — click for details`;
			}
			return 'Quantity short — click for details';
		}
		return msg.slice(0, 65).trim() + '… — click for details';
	}

	private getErrorCode(item: ExportQueueItem): string | undefined {
		const response = item.export_response as any;
		if (response && typeof response === 'object') {
			if (typeof response.error_code === 'string' && response.error_code) {
				return response.error_code;
			}
			if (typeof response.code === 'string' && response.code) {
				return response.code;
			}
		}
		return undefined;
	}

	canRetry(item: ExportQueueItem): boolean {
		if (item.export_status !== 'failed') {
			return false;
		}
		const errorCode = this.getErrorCode(item);
		if (!errorCode) {
			return true;
		}
		const unrecoverable = ['NO_ITEMS', 'STATUS_BLOCKED', 'STATUS_NOT_ELIGIBLE', 'STATUS_ALREADY_SHIPPED'];
		return unrecoverable.indexOf(errorCode) === -1;
	}

	openForceErpStatusDialog(item: ExportQueueItem): void {
		const statuses = this.orderStatusesForSelect;
		if (!statuses.length) {
			this.globalSearchService.showNotification('Order statuses not loaded yet — wait a moment and try again', 'warning', 'bottom', 'right');
			return;
		}
		if (!item?.orderno) {
			return;
		}
		this.dialog.open(ForceErpStatusDialogComponent, {
			width: '440px',
			maxWidth: '96vw',
			data: {
				orderno: item.orderno,
				currentStatusId: item.erp_order_status_id || '',
				statuses,
			} as ForceErpStatusDialogData,
		}).afterClosed().subscribe((r) => {
			if (r?.success && r.erp_order_status_id != null) {
				item.erp_order_status_id = r.erp_order_status_id;
				item.erp_order_status_name = r.erp_order_status_name != null ? r.erp_order_status_name : item.erp_order_status_name;
			}
		});
	}

	openErrorDetails(item: ExportQueueItem): void {
		if (!item.error_message && !item.export_response) {
			this.selectedErrorItem = null;
			this.selectedErrorDetails = null;
			return;
		}

		const errorCode = this.getErrorCode(item);
		const canRetry = this.canRetry(item);
		const response = item.export_response as any;

		this.selectedErrorItem = item;
		this.selectedErrorDetails = {
			message: item.error_message || (response && (response.message as string)) || '',
			errorCode,
			canRetry,
			response: response && (response.response ?? response.raw_response ?? response),
			circuitOpen: !!(response && response.circuit_open)
		};
	}

	clearErrorDetails(): void {
		this.selectedErrorItem = null;
		this.selectedErrorDetails = null;
	}

	openResponseDetails(item: ExportQueueItem): void {
		if (!item) { return; }
		if (item.export_response) {
			this.dialog.open(RawResponseModalComponent, {
				width: '720px',
				maxWidth: '96vw',
				maxHeight: '90vh',
				data: {
					orderno: item.orderno,
					export_status: item.export_status,
					content: item.export_response,
					onSent: () => this.loadQueue(true)
				},
				panelClass: 'raw-response-dialog'
			});
			return;
		}
		this.previewLoadingOrderNo = item.orderno;
		this.tpl3plService.getExportPreview(item.orderno).subscribe({
			next: (res) => {
				this.previewLoadingOrderNo = null;
				if (res.success && res.request_payload) {
					this.dialog.open(RawResponseModalComponent, {
						width: '720px',
						maxWidth: '96vw',
						maxHeight: '90vh',
						data: {
							orderno: item.orderno,
							export_status: item.export_status,
							content: { request_payload: res.request_payload },
							onSent: () => this.loadQueue(true)
						},
						panelClass: 'raw-response-dialog'
					});
				} else {
					this.globalSearchService.showNotification(res.message || 'Could not load preview', 'warning', 'bottom', 'right');
				}
			},
			error: (err) => {
				this.previewLoadingOrderNo = null;
				this.globalSearchService.showNotification(err?.error?.message || 'Could not load preview', 'danger', 'bottom', 'right');
			}
		});
	}

	archiveQueueItem(item: ExportQueueItem): void {
		if (item.export_status !== 'exported') { return; }
		this.tpl3plService.archiveQueueItem(item.id).subscribe({
			next: (result) => {
				if (result.success) {
					this.globalSearchService.showNotification('Order archived', 'success', 'bottom', 'right');
					this.loadQueue(true);
				} else {
					this.globalSearchService.showNotification(result.message || 'Could not archive', 'danger', 'bottom', 'right');
				}
			},
			error: (err) => this.globalSearchService.showNotification(err?.error?.message || 'Error archiving', 'danger', 'bottom', 'right')
		});
	}

	unarchiveQueueItem(item: ExportQueueItem): void {
		this.tpl3plService.unarchiveQueueItem(item.id, item.orderno).subscribe({
			next: (result) => {
				if (result.success) {
					this.globalSearchService.showNotification('Order restored to exported state', 'success', 'bottom', 'right');
					this.loadQueue(true);
				} else {
					this.globalSearchService.showNotification(result.message || 'Could not unarchive', 'danger', 'bottom', 'right');
				}
			},
			error: (err) => this.globalSearchService.showNotification(err?.error?.message || 'Error unarchiving', 'danger', 'bottom', 'right')
		});
	}

	checkPgsStatus(item: ExportQueueItem): void {
		if (!item?.orderno) { return; }
		this.globalSearchService.showNotification('Fetching PGS status…', 'info', 'bottom', 'right');
		this.tpl3plService.getPgsOrderStatus(item.orderno).subscribe({
			next: (result) => {
				const content = result.success
					? { response: result.data, channel_used: result.channel_used }
					: { response: { message: result.message, b2c_error: result.b2c_error, b2b_error: result.b2b_error }, channel_used: result.channel_used };
				this.dialog.open(RawResponseModalComponent, {
					width: '720px',
					maxWidth: '96vw',
					maxHeight: '90vh',
					data: {
						context: 'open_orders',
						orderno: item.orderno,
						content,
					},
					panelClass: 'raw-response-dialog'
				});
			},
			error: (err) => {
				const msg = err?.error?.message || err?.message || 'Could not fetch PGS status';
				this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
				this.dialog.open(RawResponseModalComponent, {
					width: '720px',
					maxWidth: '96vw',
					maxHeight: '90vh',
					data: {
						context: 'open_orders',
						orderno: item.orderno,
						content: { response: { success: false, message: msg, error: err?.error } },
					},
					panelClass: 'raw-response-dialog'
				});
			}
		});
	}

	massUpdatePgsStatus(): void {
		if (this.massStatusUpdating) { return; }
		const batchSize = Math.max(1, Math.min(100, Number(this.pgsMassBatchSize) || 25));
		this.massStatusUpdating = true;
		this.massStatusResult = null;
		this.massStatusProgress = 'Starting…';

		const totals: { checked: number; updated: number; unchanged: number; failed: number; erp_status_updated: number; details: any[]; batch_count: number } = {
			checked: 0,
			updated: 0,
			unchanged: 0,
			failed: 0,
			erp_status_updated: 0,
			details: [],
			batch_count: 0
		};
		let offset = 0;
		const betweenBatchMs = 350;

		const runBatch = (): void => {
			this.tpl3plService.massUpdatePgsStatus(batchSize, offset).subscribe({
				next: (result) => {
					if (!result?.success) {
						this.massStatusUpdating = false;
						this.massStatusProgress = '';
						this.globalSearchService.showNotification(result?.message || 'PGS batch failed', 'danger', 'bottom', 'right');
						return;
					}
					totals.batch_count++;
					totals.checked += result.checked;
					totals.updated += result.updated;
					totals.unchanged += result.unchanged;
					totals.failed += result.failed;
					totals.erp_status_updated += result.erp_status_updated ?? 0;
					if (result.details?.length) {
						totals.details.push(...result.details);
					}
					this.massStatusProgress = `Processed ${totals.checked} order(s) in ${totals.batch_count} batch(es)…`;

					const hasMore = !!result.has_more && result.checked > 0;
					if (hasMore) {
						offset += result.checked;
						window.setTimeout(() => runBatch(), betweenBatchMs);
						return;
					}

					this.massStatusUpdating = false;
					this.massStatusProgress = '';
					this.massStatusResult = totals;
					const erpPart = totals.erp_status_updated > 0 ? `, ${totals.erp_status_updated} ERP status(es) updated` : '';
					const msg = `PGS status refreshed: ${totals.checked} checked (${totals.batch_count} batch(es)), ${totals.updated} queue state(s) updated${erpPart}, ${totals.failed} failed`;
					this.globalSearchService.showNotification(msg, totals.failed > 0 ? 'warning' : 'success', 'bottom', 'right');
					if (totals.updated > 0 || totals.erp_status_updated > 0) {
						this.loadQueue(true);
					}
				},
				error: (err) => {
					this.massStatusUpdating = false;
					this.massStatusProgress = '';
					this.globalSearchService.showNotification(err?.error?.message || 'Mass status update failed', 'danger', 'bottom', 'right');
				}
			});
		};

		runBatch();
	}

	archiveAllExported(): void {
		this.tpl3plService.archiveAllExported().subscribe({
			next: (result) => {
				if (result.success) {
					this.globalSearchService.showNotification(result.message || 'Exported orders archived', 'success', 'bottom', 'right');
					this.loadQueue(true);
				} else {
					this.globalSearchService.showNotification(result.message || 'Could not archive', 'danger', 'bottom', 'right');
				}
			},
			error: (err) => this.globalSearchService.showNotification(err?.error?.message || 'Error archiving', 'danger', 'bottom', 'right')
		});
	}

	viewLogs(item: ExportQueueItem): void {
		this.navigateToTab('logs', item.orderno);
	}

	viewShipments(item: ExportQueueItem): void {
		this.navigateToTab('shipments', item.orderno);
	}

	private navigateToTab(tab: 'logs' | 'shipments', orderno: string): void {
		this.router.navigate(['../' + tab], { relativeTo: this.route, queryParams: { orderno } });
	}

	deleteQueueItem(item: ExportQueueItem): void {
		this.tpl3plService.deleteQueueItem(item.id).subscribe({
			next: (result) => {
				if (result.success) {
					this.globalSearchService.showNotification('Queue item removed', 'success', 'bottom', 'right');
					this.allItems = this.allItems.filter(i => i.id !== item.id);
					this.applyFilters();
					this.refreshQueueStats();
					if (this.selectedErrorItem?.id === item.id) {
						this.clearErrorDetails();
					}
				} else {
					this.globalSearchService.showNotification(result.message || 'Could not remove item', 'danger', 'bottom', 'right');
				}
			},
			error: (error) => {
				this.globalSearchService.showNotification(error?.error?.message || 'Error removing queue item', 'danger', 'bottom', 'right');
			}
		});
	}

	viewOrder(orderno: string): void {
		if (!orderno) { return; }
		this.dialog.open(OrderDetailDialogComponent, {
			data: { orderno },
			width: '90vw',
			maxWidth: '1100px',
			maxHeight: '90vh',
			panelClass: 'order-detail-panel',
		});
	}
}
